import { useCallback, useState } from "react";
import {
  archiveManufacturingInventoryCountSession,
  closeManufacturingInventoryCountSession,
  createManufacturingInventoryCountProposal,
  getOpenManufacturingInventoryCountSession,
  listManufacturingInventoryCountLines,
  listManufacturingInventoryCountProposals,
  openManufacturingInventoryCountSession,
  reviewManufacturingInventoryCountProposal,
  updateManufacturingInventoryCountLine,
} from "@/lib/manufacturing/inventoryCountClient";
import {
  buildInventoryCountOfflineDraft,
  clearInventoryCountOfflineDraft,
  saveInventoryCountOfflineDraft,
} from "@/lib/manufacturing/inventoryCountOfflineDraft";
import type {
  MfgInventoryCountLine,
  MfgInventoryCountProposal,
  MfgInventoryCountSession,
} from "@/lib/manufacturing/types/inventoryCount";

/**
 * MIC-1–3 — count session + line qty + new-item proposals.
 * MIC-5 — persist local JSON draft while counting; clear on successful close.
 */
export function useManufacturingInventoryCount(opts: { enabled: boolean; plantId?: string }) {
  const { enabled, plantId } = opts;
  const [session, setSession] = useState<MfgInventoryCountSession | null>(null);
  const [lines, setLines] = useState<MfgInventoryCountLine[]>([]);
  const [proposals, setProposals] = useState<MfgInventoryCountProposal[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [proposing, setProposing] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const persistDraft = useCallback(
    (nextSession: MfgInventoryCountSession, nextLines: MfgInventoryCountLine[]) => {
      const orgId = nextSession.organization_id;
      const pid = plantId || nextSession.plant_id;
      if (!orgId || !pid) return;
      saveInventoryCountOfflineDraft(
        buildInventoryCountOfflineDraft({
          organizationId: orgId,
          plantId: pid,
          session: nextSession,
          lines: nextLines,
        }),
      );
    },
    [plantId],
  );

  const clearDraft = useCallback(
    (s: MfgInventoryCountSession | null) => {
      if (!s) return;
      const pid = plantId || s.plant_id;
      if (!s.organization_id || !pid) return;
      clearInventoryCountOfflineDraft(s.organization_id, pid);
    },
    [plantId],
  );

  const loadLines = useCallback(
    async (sessionId: string) => {
      const r = await listManufacturingInventoryCountLines({
        session_id: sessionId,
        plant_id: plantId,
      });
      if (!r.ok) {
        setError(r.error);
        return { ok: false as const, error: r.error };
      }
      setSession(r.data.session);
      setLines(r.data.lines);
      persistDraft(r.data.session, r.data.lines);
      setError(null);
      return { ok: true as const };
    },
    [plantId, persistDraft],
  );

  const loadProposals = useCallback(async (sessionId?: string) => {
    const r = await listManufacturingInventoryCountProposals(
      sessionId ? { session_id: sessionId } : { status: "pending" },
    );
    if (!r.ok) {
      // Do not block an otherwise-usable count session on the proposals lane.
      setProposals([]);
      return { ok: false as const, error: r.error };
    }
    setProposals(r.data.proposals);
    return { ok: true as const };
  }, []);

  const resumeOrNull = useCallback(async () => {
    if (!enabled) return { ok: true as const, session: null as MfgInventoryCountSession | null };
    setBusy(true);
    setError(null);
    const r = await getOpenManufacturingInventoryCountSession(
      plantId ? { plant_id: plantId } : undefined,
    );
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return { ok: false as const, error: r.error };
    }
    if (r.data.session) {
      setSession(r.data.session);
      await loadLines(r.data.session.id);
      await loadProposals(r.data.session.id);
    } else {
      setSession(null);
      setLines([]);
      setProposals([]);
    }
    return { ok: true as const, session: r.data.session };
  }, [enabled, plantId, loadLines, loadProposals]);

  const open = useCallback(async () => {
    if (!enabled) return { ok: false as const, error: "Manufacturing entitlement required" };
    setBusy(true);
    setError(null);
    const r = await openManufacturingInventoryCountSession(
      plantId ? { plant_id: plantId } : undefined,
    );
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return { ok: false as const, error: r.error };
    }
    setSession(r.data.session);
    await loadLines(r.data.session.id);
    await loadProposals(r.data.session.id);
    return { ok: true as const, session: r.data.session, line_count: r.data.line_count };
  }, [enabled, plantId, loadLines, loadProposals]);

  const close = useCallback(async () => {
    if (!session) return { ok: false as const, error: "No open session" };
    setBusy(true);
    setError(null);
    const r = await closeManufacturingInventoryCountSession(session.id);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      // Keep draft so the operator can download while offline / failed send.
      persistDraft(session, lines);
      return { ok: false as const, error: r.error };
    }
    clearDraft(session);
    setSession(r.data.session);
    return {
      ok: true as const,
      session: r.data.session,
      reconciliation: r.data.reconciliation ?? null,
    };
  }, [session, lines, persistDraft, clearDraft]);

  const archive = useCallback(async () => {
    if (!session) return { ok: false as const, error: "No open session" };
    setBusy(true);
    setError(null);
    const r = await archiveManufacturingInventoryCountSession(session.id);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      persistDraft(session, lines);
      return { ok: false as const, error: r.error };
    }
    clearDraft(session);
    setSession(r.data.session);
    return { ok: true as const, session: r.data.session };
  }, [session, lines, persistDraft, clearDraft]);

  const setLineQty = useCallback(
    async (lineId: string, countedQty: number | null) => {
      setSavingLineId(lineId);
      setError(null);
      const r = await updateManufacturingInventoryCountLine({
        line_id: lineId,
        counted_qty: countedQty,
      });
      setSavingLineId(null);
      if (!r.ok) {
        setError(r.error);
        // Optimistic local keep + draft so counts aren't lost on spotty network.
        setLines((prev) => {
          const next = prev.map((l) =>
            l.id === lineId
              ? {
                  ...l,
                  counted_qty: countedQty,
                  counted_at: countedQty == null ? null : new Date().toISOString(),
                }
              : l,
          );
          setSession((s) => {
            if (s) persistDraft(s, next);
            return s;
          });
          return next;
        });
        return { ok: false as const, error: r.error };
      }
      setLines((prev) => {
        const next = prev.map((l) => (l.id === r.data.line.id ? r.data.line : l));
        persistDraft(r.data.session, next);
        return next;
      });
      setSession(r.data.session);
      return { ok: true as const, line: r.data.line };
    },
    [persistDraft],
  );

  const proposeNewItem = useCallback(
    async (opts: {
      proposed_name: string;
      proposed_unit: string;
      proposed_category: string;
      proposed_qty: number | null;
    }) => {
      if (!session) return { ok: false as const, error: "No open session" };
      setProposing(true);
      setError(null);
      const r = await createManufacturingInventoryCountProposal({
        session_id: session.id,
        ...opts,
      });
      setProposing(false);
      if (!r.ok) {
        setError(r.error);
        return { ok: false as const, error: r.error };
      }
      setProposals((prev) => [r.data.proposal, ...prev]);
      return { ok: true as const, proposal: r.data.proposal };
    },
    [session],
  );

  const reviewProposal = useCallback(
    async (proposalId: string, action: "approve" | "reject") => {
      setReviewingId(proposalId);
      setError(null);
      const r = await reviewManufacturingInventoryCountProposal({
        proposal_id: proposalId,
        action,
      });
      setReviewingId(null);
      if (!r.ok) {
        setError(r.error);
        return { ok: false as const, error: r.error };
      }
      setProposals((prev) =>
        prev.map((p) => (p.id === r.data.proposal.id ? r.data.proposal : p)),
      );
      if (action === "approve" && session) {
        await loadLines(session.id);
      }
      return { ok: true as const, proposal: r.data.proposal };
    },
    [session, loadLines],
  );

  const reset = useCallback(() => {
    setSession(null);
    setLines([]);
    setProposals([]);
    setError(null);
    setSavingLineId(null);
    setProposing(false);
    setReviewingId(null);
  }, []);

  return {
    session,
    lines,
    proposals,
    busy,
    savingLineId,
    proposing,
    reviewingId,
    error,
    open,
    close,
    archive,
    resumeOrNull,
    loadLines,
    loadProposals,
    setLineQty,
    proposeNewItem,
    reviewProposal,
    reset,
    clearError: () => setError(null),
  };
}
